/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import type { ButtonProps, Theme } from '@coursera/cds-core';
import { Button, Typography, Typography2 } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import {
  FULLNAME_CONSTRAINT,
  HANDLED_ERRORS,
  PASSWORD_CONSTRAINT,
  RECAPTCHA_ACTIONS,
  existingEmailError,
  responseMessageFactory,
} from 'bundles/authentication/constants';
import Alert from 'bundles/authentication/shared/Alert';
import Form from 'bundles/authentication/shared/Form';
import NameInput from 'bundles/authentication/shared/NameInput';
import PasswordInput from 'bundles/authentication/shared/PasswordInput';
import * as actions from 'bundles/authentication/shared/actions';
import LinkButton from 'bundles/authentication/shared/components/authentication-modal/LinkButton';
import { useRecaptcha } from 'bundles/authentication/shared/hooks/useRecaptcha';
import type { ErrorResponse, EventData, SuccessResponse } from 'bundles/authentication/shared/types/sharedTypes';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import GrowthAcquisitionExperiments from 'bundles/epic/clients/GrowthAcquisition';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { trackAuthAttempts } from 'bundles/user-account/utils/authTrackingUtils';

import _t from 'i18n!nls/authentication';

import { MODES } from './AuthenticationContent';

export type PropsFromCaller = {
  // Necessary to trigger signup
  email: string;
  onRegistrationSuccess: (res: SuccessResponse) => void;
  navigate: (path: MODES, options?: $TSFixMe) => void;
  trackingName?: string;
};

type Context = {
  _eventData: EventData;
};

const TrackedLinkButton = withSingleTracked({ type: 'BUTTON' })(LinkButton);

const styles = {
  form: (theme: Theme) => css`
    margin: auto;
    padding: ${theme.spacing(24)};
    ${theme.breakpoints.down('sm')} {
      width: 100%;
    }
  `,
  tos: (theme: Theme) => css`
    margin-top: ${theme.spacing(24)};
  `,
  text: css`
    text-align: center;
    display: block;
  `,
  supportLinks: (theme: Theme) => css`
    text-decoration: underline;
    color: ${theme.palette.gray[700]};
  `,
  heading: (theme: Theme) => css`
    text-align: center;
    margin-bottom: ${theme.spacing(16)};
  `,
  nameInput: (theme: Theme) => css`
    margin-bottom: ${theme.spacing(8)};
  `,
  navigation: (theme: Theme) => css`
    margin: ${theme.spacing(16)} 0;
    text-align: center;
  `,
  submit: (theme: Theme) =>
    css({
      '&&': {
        marginTop: theme.spacing(16),
        minWidth: '100%',
        maxWidth: 'unset',
      },
    }),
  arrowIcon: css`
    padding-left: 8px;
  `,
  reCAPTCHA: css`
    display: flex;
    justify-content: center;

    &.checkbox {
      margin-top: 8px;
    }

    :nth-of-type(n) .grecaptcha-badge {
      display: none !important;
    }
  `,
};

const XDP_FASTER_SIGNUP_RECAPTCHA_ID = 'recaptcha-xdp-signup';

const TrackedSubmit = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const SetPassword = (
  { email, navigate, onRegistrationSuccess, trackingName = 'set_password_form' }: PropsFromCaller,
  { _eventData }: Context
) => {
  const [password, setPassword] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [message, setMessage] = useState<string | JSX.Element>('');
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const isCheckbox = GrowthAcquisitionExperiments.get('useEnterpriseCheckboxReCaptchaSignup');
  const recaptcha = useRecaptcha(XDP_FASTER_SIGNUP_RECAPTCHA_ID, {
    checkbox: isCheckbox,
    action: RECAPTCHA_ACTIONS.setPassword,
  });

  useEffect(() => {
    passwordInputRef.current = document.getElementById('password') as HTMLInputElement;
    passwordInputRef.current?.focus();
  }, []);

  const messages = responseMessageFactory();

  const handleSuccess = (res: SuccessResponse) => {
    const successMsg = messages.success;
    setMessage(successMsg);
    onRegistrationSuccess(res);
  };

  const handleError = (response: ErrorResponse) => {
    recaptcha?.reset();

    const { errorCode = '' } = response?.responseJSON || {};
    if (HANDLED_ERRORS.includes(errorCode)) {
      if (errorCode === existingEmailError) {
        const navigateToLogin = () => {
          navigate(MODES.LOGIN);
        };
        const errMessageForExistingAcc = messages[errorCode](navigateToLogin);
        setMessage(errMessageForExistingAcc);
        setIsRunning(false);
      } else {
        const msg = messages[errorCode as Exclude<keyof typeof messages, 'existingCourseraAccount'>];
        setMessage(msg);
        setIsRunning(false);
      }
    } else {
      setMessage(
        <FormattedMessage
          message={_t('An unexpected error has occurred. Please try again or contact our {helpCenterLink}.')}
          helpCenterLink={<a href="https://courserahelp.zendesk.com">{_t('Learner Help Center')}</a>}
        />
      );
      // so the submit button isn't permanently greyed out
      setIsRunning(false);
    }
  };

  const handleSignup = async () => {
    const token = await recaptcha?.getResponse();

    if (!token) {
      const msg = isCheckbox ? messages.missingReCaptcha : '';
      return setMessage(msg);
    }
    setIsRunning(true);
    trackAuthAttempts(_eventData, 'signup');

    try {
      // There is no enterprise user in this modal
      const response = await actions.signup({ email, password, token });
      handleSuccess(response as SuccessResponse);
    } catch (error) {
      handleError(error as ErrorResponse);
    }
  };

  const navigateToLogin = useCallback(() => {
    navigate(MODES.LOGIN, { defaultEmail: email });
  }, [email, navigate]);

  const onInvalid = () => {
    setMessage('');
  };

  const constraints: Record<string, (arg: string) => string | false> = {
    name: FULLNAME_CONSTRAINT,
    password: PASSWORD_CONSTRAINT,
  };

  return (
    <TrackedDiv trackingName={trackingName} withVisibilityTracking>
      <Form
        css={styles.form}
        constraints={constraints}
        name="set-password-signup"
        onSubmit={handleSignup}
        onInvalid={onInvalid}
      >
        <Typography data-test="setPwHeader" css={styles.heading} variant="h1semibold">
          {_t('Let’s finish setting up your account.')}
        </Typography>
        {message && (
          <Alert role="alert" type={message === messages.success ? 'success' : 'error'}>
            {message}
          </Alert>
        )}

        <NameInput css={styles.nameInput} autoFocus required ref={nameInputRef} autoComplete="name" />

        <PasswordInput
          value={password}
          onChange={(ev) => setPassword(ev.target.value)}
          hint={_t('Between 8 and 72 characters')}
          autoComplete="new-password"
          required
        />

        <div css={styles.reCAPTCHA} id={XDP_FASTER_SIGNUP_RECAPTCHA_ID} data-size={!isCheckbox && 'invisible'} />

        <TrackedSubmit
          {...Button.defaultProps}
          trackingName="signup_form_submit_button"
          disabled={isRunning}
          css={styles.submit}
          type="submit"
        >
          {_t('Enroll for Free')}
          <ArrowNextIcon size="large" css={styles.arrowIcon} />
        </TrackedSubmit>

        <p css={styles.navigation}>
          <FormattedMessage
            message={_t('Already on Coursera? {loginLink}')}
            loginLink={
              <TrackedLinkButton onClick={navigateToLogin} trackingName="login">
                {_t('Log in')}
              </TrackedLinkButton>
            }
          />
        </p>

        <Typography2 color="supportText" component="span" css={[styles.text, styles.tos]}>
          <FormattedMessage
            message={_t("I accept Coursera's {tosLink} and {privacyLink}.")}
            privacyLink={
              <a css={styles.supportLinks} href="/about/privacy" target="_blank">
                {_t('Privacy Notice')}
              </a>
            }
            tosLink={
              <a css={styles.supportLinks} href="/about/terms" target="_blank">
                {_t('Terms of Use')}
              </a>
            }
          />
        </Typography2>

        <Typography2 color="supportText" css={styles.text} component="span">
          <FormattedMessage
            message={_t(
              'This site is protected by reCAPTCHA Enterprise and the Google {privacyPolicyLink} and {tosLink} apply.'
            )}
            privacyPolicyLink={
              <a
                css={styles.supportLinks}
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                {_t('Privacy Policy')}
              </a>
            }
            tosLink={
              <a
                css={styles.supportLinks}
                href="https://policies.google.com/terms"
                target="_blank"
                rel="noopener noreferrer"
              >
                {_t('Terms of Service')}
              </a>
            }
          />
        </Typography2>
      </Form>
    </TrackedDiv>
  );
};

SetPassword.contextTypes = {
  _eventData: PropTypes.object.isRequired,
};

export default SetPassword;
